import { compact, groupBy, isEmpty, some, sortBy } from "lodash/fp";

import { EmailDigest } from "@app/analytics/types";
import { PartialBy, User, UserWorkspace } from "@app/common/types";
import { populateUser } from "@app/common/utils";
import {
  ApiEnterpriseTeamUser,
  ApiEnterpriseWorkspace,
  ApiEnterpriseWorkspaceInvite,
  ApiEnterpriseWorkspaceUser,
  EnterpriseRole,
  EnterpriseTeam,
  EnterpriseTeamUser,
  EnterpriseUser,
  EnterpriseWorkspace,
  EnterpriseWorkspaceInvite,
  EnterpriseWorkspaceTeam,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";

export const populateUsers = (
  workspace: ApiEnterpriseWorkspace
): PartialBy<EnterpriseWorkspace, "invites"> => {
  const populatedTeams: EnterpriseWorkspaceTeam[] = workspace.teams.map(
    (team) => {
      const populatedTeamUsers = team.users.map((user) => ({
        ...populateUser<ApiEnterpriseTeamUser>(user),
        type: "user" as EnterpriseUser["type"],
      }));

      return {
        ...team,
        users: populatedTeamUsers,
        invites: [], // this will get populated in the populateInvites method
      };
    }
  );

  const populatedWorkspaceUsers: EnterpriseWorkspaceUser[] =
    workspace.users.map((workspaceUser) => {
      const workspaceUserTeams: EnterpriseWorkspaceTeam[] = sortBy(
        "name",
        compact(
          populatedTeams.map((team) => {
            const teamUsers: EnterpriseTeamUser[] = team.users.filter(
              (teamUser) => teamUser.userId === workspaceUser.userId
            );

            return isEmpty(teamUsers)
              ? null
              : { ...team, users: teamUsers, invites: [] };
          })
        )
      );

      return {
        ...populateUser<ApiEnterpriseWorkspaceUser>(workspaceUser),
        teams: workspaceUserTeams,
        type: "user" as EnterpriseUser["type"],
      };
    });

  return {
    ...workspace,
    users: populatedWorkspaceUsers,
    teams: populatedTeams,
    invites: [], // this will get populated in the populateInvites method
  };
};

export const populateInvites = (
  workspace: PartialBy<EnterpriseWorkspace, "invites">,
  invites: ApiEnterpriseWorkspaceInvite[]
): EnterpriseWorkspace => {
  const validInvites = invites.filter((invite) => invite.status === "pending");

  const invitesByTeam = groupBy("teamId", validInvites);
  const populatedTeams: EnterpriseWorkspaceTeam[] = workspace.teams.map(
    (team) => ({
      ...team,
      invites: (invitesByTeam[team.id] ?? []).map((invite) => ({
        ...invite,
        type: "invite" as EnterpriseWorkspaceInvite["type"],
      })),
    })
  );

  const populatedInvites: EnterpriseWorkspaceInvite[] = validInvites.map(
    (invite) => {
      const team = invite.teamId
        ? workspace.teams.find((team) => team.id === invite.teamId)
        : undefined;
      return {
        ...invite,
        type: "invite" as EnterpriseWorkspaceInvite["type"],
        teams: compact([team]),
      };
    }
  );

  return {
    ...workspace,
    teams: populatedTeams,
    invites: populatedInvites,
  };
};

export const populateWorkspace = (
  workspace: ApiEnterpriseWorkspace,
  invites: ApiEnterpriseWorkspaceInvite[]
): EnterpriseWorkspace => {
  const workspaceWithPopulatedUsers = populateUsers(workspace);
  return populateInvites(workspaceWithPopulatedUsers, invites);
};

export const EMAIL_DIGEST_ID_BY_NAME: { [key in EmailDigest]: number } = {
  "Meeting Recaps": 26462,
  "Weekly Recaps": 26468,
};

export const isTeamManager = (
  user: EnterpriseTeamUser | EnterpriseWorkspaceUser
) =>
  "teams" in user
    ? some({ role: "manager" }, user.teams)
    : user.role === "manager";

export const isWorkspaceOwner = (
  userOrUserWorkspace?: EnterpriseWorkspaceUser | UserWorkspace
) => {
  if (userOrUserWorkspace == null) return false;
  return userOrUserWorkspace.role === "owner";
};

export const isWorkspaceAdmin = (
  userOrUserWorkspace?: EnterpriseWorkspaceUser | UserWorkspace
) => {
  if (userOrUserWorkspace == null) return false;
  return (
    userOrUserWorkspace.role === "owner" || userOrUserWorkspace.role === "admin"
  );
};

export const isWorkspaceMember = (
  userOrUserWorkspace?: EnterpriseWorkspaceUser | UserWorkspace
) => {
  if (userOrUserWorkspace == null) return false;
  return userOrUserWorkspace.role === "member";
};

export const userIsTheOnlyTeamManager = (
  userId: EnterpriseUser["userId"],
  workspace: EnterpriseWorkspace,
  teamId?: EnterpriseTeam["id"]
) => {
  if (teamId) {
    const team = workspace.teams.find((team) => team.id === teamId);

    if (team) {
      const teamManagers = team.users.filter(isTeamManager);
      const userTeamManager = teamManagers.find(
        (manager) => userId === manager.userId
      );

      if (userTeamManager) {
        return teamManagers.length === 1;
      }
    }
  }

  return false;
};

export const teamsWhereUserIsTheOnlyManager = (
  workspace: EnterpriseWorkspace,
  userId: User["id"]
) => {
  return workspace.teams.filter((team) =>
    userIsTheOnlyTeamManager(userId, workspace, team.id)
  );
};

export const findMyRoleInTeam = (
  users: EnterpriseTeamUser[],
  userId: string
): EnterpriseRole => {
  const userInTeam = users.find((user) => user.userId === userId);

  return userInTeam?.role ?? "member";
};
